import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { HttpService } from 'src/app/services/http-service.service';
import {
  getProfileCompletionPercentage,
  getProfileCompletionSteps,
  isProfileComplete,
  ProfileCompletionStep,
} from '../utils/profile-completion.util';

const SNOOZE_KEY_PREFIX = 'incompleteProfileBannerSnoozeUntil:';
const SNOOZE_MS = 10 * 60 * 1000;
const PROFILE_REWARD_POINTS = 500;

@Component({
  selector: 'app-incomplete-profile-banner',
  templateUrl: './incomplete-profile-banner.component.html',
  styleUrls: ['./incomplete-profile-banner.component.scss'],
})
export class IncompleteProfileBannerComponent implements OnInit, OnDestroy {
  visible = false;
  completionPercentage = 0;
  steps: ProfileCompletionStep[] = [];
  readonly rewardPoints = PROFILE_REWARD_POINTS;

  private authSub?: Subscription;
  private userSub?: Subscription;
  private snoozeTimerId?: ReturnType<typeof setTimeout>;
  private userId: number | string | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly http: HttpService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.authSub = this.authService.userIsAuthenticated$
      .pipe(distinctUntilChanged((a, b) => a?.token === b?.token))
      .subscribe((auth) => {
        if (!auth?.token) {
          this.hide();
          return;
        }
        this.refreshFromStorage();
        this.fetchUser();
      });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    this.userSub?.unsubscribe();
    if (this.snoozeTimerId !== undefined) {
      clearTimeout(this.snoozeTimerId);
    }
  }

  completeAndEarn(): void {
    void this.router.navigate(['/profile-new']);
  }

  snooze(): void {
    if (this.userId == null) {
      this.visible = false;
      return;
    }
    const until = Date.now() + SNOOZE_MS;
    localStorage.setItem(this.snoozeKey(this.userId), String(until));
    this.visible = false;
    this.scheduleSnoozeWake(until);
  }

  private fetchUser(): void {
    this.userSub?.unsubscribe();
    this.userSub = this.http.getCurrentUser().subscribe({
      next: (user) => {
        this.applyUser(user as Record<string, unknown>);
      },
      error: () => {
        this.refreshFromStorage();
      },
    });
  }

  private refreshFromStorage(): void {
    try {
      const user = JSON.parse(
        localStorage.getItem('current') || '{}',
      ) as Record<string, unknown>;
      this.applyUser(user);
    } catch {
      this.hide();
    }
  }

  private applyUser(user: Record<string, unknown>): void {
    this.userId = (user?.['id'] as number | string) ?? null;
    this.steps = getProfileCompletionSteps(user);
    this.completionPercentage = getProfileCompletionPercentage(user);

    if (!this.userId || isProfileComplete(user) || this.isSnoozed(this.userId)) {
      this.visible = false;
      if (this.userId && this.isSnoozed(this.userId)) {
        const until = Number(
          localStorage.getItem(this.snoozeKey(this.userId)) || 0,
        );
        this.scheduleSnoozeWake(until);
      }
      return;
    }

    this.visible = true;
  }

  private isSnoozed(userId: number | string): boolean {
    const until = Number(localStorage.getItem(this.snoozeKey(userId)) || 0);
    return until > Date.now();
  }

  private scheduleSnoozeWake(until: number): void {
    if (this.snoozeTimerId !== undefined) {
      clearTimeout(this.snoozeTimerId);
      this.snoozeTimerId = undefined;
    }
    const delay = until - Date.now();
    if (delay <= 0) {
      return;
    }
    this.snoozeTimerId = setTimeout(() => {
      this.refreshFromStorage();
    }, delay);
  }

  private snoozeKey(userId: number | string): string {
    return `${SNOOZE_KEY_PREFIX}${userId}`;
  }

  private hide(): void {
    this.visible = false;
    this.steps = [];
    this.completionPercentage = 0;
  }
}
